import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Button, InputField, Row, SaladTable, ViewFieldType } from 'saladsoft-primitive';
import {
  AdminCouponDto,
  AdminUserCouponDto,
  AdminUserCouponRequestDto,
} from 'services/data-contracts';
import ServiceHelper from 'stores/ServiceHelper';

const CouponView = ({
  rootCouponId,
  coupon,
}: {
  rootCouponId?: number;
  coupon: AdminCouponDto | AdminUserCouponDto;
}) => {
  // AdminCouponDto(관리자가 생성한 전체 쿠폰 정보)와 AdminUserCouponDto(유저에게 발급된 쿠폰) 타입 구분
  const isAdminCouponDto = (data: any): data is AdminCouponDto => {
    return data && 'localizedName' in data;
  };

  const [remark, setRemark] = useState<string>();
  const [isModified, setIsModified] = useState<boolean>(false);

  const priceConverter = (data: AdminCouponDto | AdminUserCouponDto) => {
    if (isAdminCouponDto(data)) {
      return data.price
        ? `${data.price.toLocaleString()} ${data.discountType === 'PERCENT' ? '%' : '원'}`
        : '-';
    }
    return data.coupon?.price
      ? `${data.coupon.price.toLocaleString()} ${data.coupon.discountType === 'PERCENT' ? '%' : '원'}`
      : '-';
  };

  const CouponStatus: Record<string, string> = {
    OPEN: '사용가능',
    USED: '사용완료',
    DISCARDED: '기한만료',
  };

  const fields: ViewFieldType<any>[] = [
    {
      key: 'name',
      label: '쿠폰명',
      controlType: 'custom',
      template: (
        <div>
          {isAdminCouponDto(coupon) ? coupon.localizedName?.ko || '-' : coupon.coupon?.name || '-'}
        </div>
      ),
    },
    {
      key: 'desc',
      label: '쿠폰설명',
      controlType: 'custom',
      template: (
        <div>
          {isAdminCouponDto(coupon) ? coupon.localizedDesc?.ko || '-' : coupon.coupon?.desc || '-'}
        </div>
      ),
    },
    {
      key: 'discountType',
      label: '할인 타입',
      controlType: 'custom',
      template: (
        <div>
          {isAdminCouponDto(coupon)
            ? coupon.discountType === 'PERCENT'
              ? '비율할인'
              : '정액할인'
            : coupon.coupon?.discountType === 'PERCENT'
              ? '비율할인'
              : '정액할인'}
        </div>
      ),
    },
    {
      key: 'price',
      label: '금액',
      controlType: 'custom',
      template: <div>{priceConverter(coupon)}</div>,
    },
    {
      key: 'status',
      label: '상태',
      controlType: 'custom',
      template: (
        <div>
          {isAdminCouponDto(coupon)
            ? coupon.expired && '만료됨'
            : CouponStatus[coupon.status as keyof typeof CouponStatus] || '-'}
        </div>
      ),
    },
    {
      key: 'stayStartDate',
      label: '숙박 시작일',
      controlType: 'custom',
      template: (
        <div>
          {isAdminCouponDto(coupon)
            ? coupon.stayStartDate
              ? new Date(coupon.stayStartDate).toLocaleDateString()
              : '-'
            : coupon.coupon?.stayStartDate
              ? new Date(coupon.coupon.stayStartDate).toLocaleDateString()
              : '-'}
        </div>
      ),
    },
    {
      key: 'stayEndDate',
      label: '숙박 종료일',
      controlType: 'custom',
      template: (
        <div>
          {isAdminCouponDto(coupon)
            ? coupon.stayEndDate
              ? new Date(coupon.stayEndDate).toLocaleDateString()
              : '-'
            : coupon.coupon?.stayEndDate
              ? new Date(coupon.coupon.stayEndDate).toLocaleDateString()
              : '-'}
        </div>
      ),
    },
    {
      key: 'expireDate',
      label: '만료일',
      controlType: 'custom',
      template: (
        <div>
          {isAdminCouponDto(coupon)
            ? coupon.expireDate
              ? new Date(coupon.expireDate).toLocaleDateString()
              : '-'
            : coupon.coupon?.expireDate
              ? new Date(coupon.coupon.expireDate).toLocaleDateString()
              : '-'}
        </div>
      ),
    },
    {
      key: 'createDate',
      label: '생성일',
      controlType: 'custom',
      template: (
        <div>{coupon.createDate ? new Date(coupon.createDate).toLocaleDateString() : '-'}</div>
      ),
    },
    {
      key: 'creator',
      label: '작성자',
      controlType: 'custom',
      template: <div>{coupon.creator?.username || '-'}</div>,
    },
    // {
    //   key: 'remark',
    //   label: '비고',
    //   controlType: 'custom',
    //   template: <div>{isAdminCouponDto(coupon) ? '' : coupon.remark}</div>,
    // },
  ];

  const handleSaveRemark = () => {
    const newData: AdminUserCouponRequestDto = {
      ...coupon,
      remark,
    };
    ServiceHelper?.adminCouponService
      ?.updateUserCoupon(rootCouponId as number, coupon.id as number, newData)
      .then((res) => {
        if (res.status === 200) {
          setIsModified(true);
          toast.success('비고 수정이 완료되었습니다.');
        }
      });
  };

  useEffect(() => {
    setRemark((coupon as any).remark);
  }, [coupon]);

  return (
    <div style={{ paddingBottom: 40 }}>
      <SaladTable.SaladView fields={fields} values={coupon} />
      {typeof rootCouponId === 'number' && (
        <Row justifyContent="flex-end" padding={20} gap={10}>
          <InputField
            placeholder="비고"
            value={remark}
            onChange={(value) => {
              setRemark(value);
            }}
            rows={2}
            multiline
          />
          <Button
            style={{ width: 120, background: 'var(--point-color)' }}
            onClick={() => {
              handleSaveRemark();
            }}
          >
            비고 수정
          </Button>
        </Row>
      )}
    </div>
  );
};

export default CouponView;
